import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Globe, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import axios from 'axios';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Languages that should only appear on Sri Lanka site
const SL_ONLY_LANGUAGES = new Set(['si', 'ta']);

const LanguageSwitcher = () => {
  const { country } = useCountry();
  const [currentLang, setCurrentLang] = useState('en');
  const [isTranslating, setIsTranslating] = useState(false);
  const [enabledLanguages, setEnabledLanguages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter languages by country: hide Sinhala/Tamil outside Sri Lanka
  const visibleLanguages = useMemo(() => {
    if (country === 'SL') return enabledLanguages;
    return enabledLanguages.filter(l => !SL_ONLY_LANGUAGES.has(l.code));
  }, [enabledLanguages, country]);

  // Fetch enabled languages from API
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/localization/languages/enabled`);
        if (response.data.success) {
          setEnabledLanguages(response.data.languages);
        }
      } catch (error) {
        console.error('Error fetching languages:', error);
        // Fallback to default languages
        setEnabledLanguages([
          { code: 'en', name: 'English', native_name: 'English', flag: '🇬🇧', enabled: true },
          { code: 'si', name: 'Sinhala', native_name: 'සිංහල', flag: '🇱🇰', enabled: true },
          { code: 'ta', name: 'Tamil', native_name: 'தமிழ்', flag: '🇮🇳', enabled: true },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchLanguages();
  }, []);

  // Check current language from Google Translate cookie on mount
  useEffect(() => {
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    };

    // Check googtrans cookie
    const googtrans = getCookie('googtrans');
    if (googtrans) {
      if (googtrans.includes('/si')) {
        setCurrentLang('si');
      } else if (googtrans.includes('/ta')) {
        setCurrentLang('ta');
      } else if (googtrans.includes('/zh-CN') || googtrans.includes('/zh')) {
        setCurrentLang('zh-CN');
      } else if (googtrans.includes('/hi')) {
        setCurrentLang('hi');
      } else {
        setCurrentLang('en');
      }
    }

    // Also check localStorage as backup
    const savedLang = localStorage.getItem('zeilian_language');
    if (savedLang && !googtrans) {
      setCurrentLang(savedLang);
    }
  }, []);

  const changeLanguage = useCallback((langCode) => {
    if (langCode === currentLang && langCode !== 'en') return;
    
    setIsTranslating(true);
    localStorage.setItem('zeilian_language', langCode);

    // Comprehensive cookie clearing function
    const clearAllGoogTransCookies = () => {
      const hostname = window.location.hostname;
      const hostParts = hostname.split('.');
      
      // Generate all possible domain variations
      const domains = ['', hostname];
      for (let i = 0; i < hostParts.length - 1; i++) {
        domains.push('.' + hostParts.slice(i).join('.'));
      }
      
      const paths = ['/', '', window.location.pathname];
      
      // Clear cookies for all domain/path combinations
      domains.forEach(domain => {
        paths.forEach(path => {
          const cookieBase = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
          const pathPart = `; path=${path || '/'}`;
          const domainPart = domain ? `; domain=${domain}` : '';
          document.cookie = cookieBase + pathPart + domainPart;
        });
      });
    };

    // Set translation cookie with all necessary variations
    const setGoogTransCookie = (targetLang) => {
      const hostname = window.location.hostname;
      const hostParts = hostname.split('.');
      const cookieValue = `/en/${targetLang}`;
      const expiry = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
      
      // Set for root path
      document.cookie = `googtrans=${cookieValue}; expires=${expiry}; path=/`;
      
      // Set for hostname
      document.cookie = `googtrans=${cookieValue}; expires=${expiry}; path=/; domain=${hostname}`;
      
      // Set for parent domains
      for (let i = 0; i < hostParts.length - 1; i++) {
        const parentDomain = '.' + hostParts.slice(i).join('.');
        document.cookie = `googtrans=${cookieValue}; expires=${expiry}; path=/; domain=${parentDomain}`;
      }
    };

    if (langCode === 'en') {
      // Reset to English - clear all cookies
      clearAllGoogTransCookies();
      localStorage.removeItem('zeilian_language');
      
      // Force full page reload without cache
      setTimeout(() => {
        window.location.href = window.location.origin + window.location.pathname;
      }, 100);
    } else {
      // First clear existing cookies
      clearAllGoogTransCookies();
      
      // Then set new translation cookies
      setTimeout(() => {
        setGoogTransCookie(langCode);
        setCurrentLang(langCode);
        
        // Reload to apply translation
        setTimeout(() => {
          window.location.reload();
        }, 150);
      }, 100);
    }
  }, [currentLang]);

  // Filter languages - Sinhala shown in menu bar, others in dropdown.
  // `visibleLanguages` already excludes Sinhala/Tamil for non-SL countries.
  const sinhalaLang = visibleLanguages.find(l => l.code === 'si');
  const otherLanguages = visibleLanguages.filter(l => l.code !== 'si');

  if (loading) {
    return (
      <div className="flex items-center">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex items-center notranslate">
      {/* Google Translate Element Container */}
      <div 
        id="google_translate_element" 
        style={{ 
          position: 'absolute', 
          top: '-9999px', 
          left: '-9999px',
          opacity: 0,
          pointerEvents: 'none'
        }} 
      />

      {/* Desktop: Sinhala Button + Globe Dropdown */}
      <div className="hidden md:flex items-center gap-2 mr-3">
        {/* Sinhala Button - Always visible in menu bar if enabled */}
        {sinhalaLang && (
          <Button
            variant={currentLang === 'si' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => changeLanguage('si')}
            disabled={isTranslating}
            className={`notranslate text-sm font-medium px-3 py-2 transition-all ${
              currentLang === 'si'
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white border-0 shadow-md rounded-full'
                : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
            }`}
          >
            {isTranslating && currentLang !== 'si' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              sinhalaLang.native_name
            )}
          </Button>
        )}

        {/* Globe Dropdown for other languages */}
        {otherLanguages.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className={`rounded-full px-2 transition-all ${
                  currentLang !== 'en' && currentLang !== 'si' 
                    ? 'bg-blue-100 text-blue-600' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {isTranslating ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Globe className="h-5 w-5" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Other Languages
              </div>
              <DropdownMenuSeparator />
              {otherLanguages.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  disabled={isTranslating}
                  className={`notranslate cursor-pointer ${
                    currentLang === lang.code ? 'bg-blue-50 text-blue-600' : ''
                  }`}
                >
                  <span className="mr-2 text-base">{lang.flag}</span>
                  <span className="flex-1">{lang.native_name}</span>
                  {currentLang === lang.code && (
                    <span className="ml-auto text-blue-600 font-bold">✓</span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Mobile: Single Globe Dropdown with all languages */}
      <div className="flex md:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="rounded-full px-2">
              {isTranslating ? (
                <Loader2 className="h-5 w-5 animate-spin text-gray-600" />
              ) : (
                <Globe className="h-5 w-5 text-gray-600" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {visibleLanguages.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                disabled={isTranslating}
                className={`notranslate cursor-pointer ${
                  currentLang === lang.code ? 'bg-blue-50 text-blue-600' : ''
                }`}
              >
                <span className="mr-2">{lang.flag}</span>
                <span className="flex-1">{lang.native_name}</span>
                {currentLang === lang.code && (
                  <span className="ml-auto text-blue-600">✓</span>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default LanguageSwitcher;
